import { useState, useEffect, memo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { DogSchool } from '@/data/dogSchools';
import { Card, CardContent } from '@/components/ui/card';
import { Star } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { SchoolBasicInfo } from './DogSchoolCard/SchoolBasicInfo';
import { SchoolFeatures } from './DogSchoolCard/SchoolFeatures';
import { SchoolActionButtons } from './DogSchoolCard/SchoolActionButtons';
import { ReviewCarousel } from './DogSchoolCard/ReviewCarousel';

interface DogSchoolCardProps {
  school: DogSchool;
  distance?: number; // Make distance optional
}

const DogSchoolCardComponent: React.FC<DogSchoolCardProps> = ({ school, distance }) => {
  const { t } = useLanguage();
  const [imageError, setImageError] = useState(false);
  const [reviewData, setReviewData] = useState<{
    reviews: string[];
    reviewCount: number;
    googleReview: number;
  }>({
    reviews: school.reviews || [],
    reviewCount: school.reviewCount || 0,
    googleReview: school.googleReview || 4.5
  });
  
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        if (school.id) {
          console.log(`Fetching reviews for school with google_id: ${school.id}`);
          
          const { data: reviewData, error: reviewError } = await supabase
            .from('hundeschuleReview')
            .select('*, "google_maps_reviews.google_maps_reviews.reviews", "google_maps_reviews.google_maps_reviews.rating"')
            .eq('google_id', school.id)
            .maybeSingle();
          
          if (reviewError) {
            console.error("Error fetching reviews:", reviewError);
          }
          
          if (reviewData) {
            console.log("Found review data:", reviewData);
            
            const reviewsArray: string[] = [];
            
            for (let i = 1; i <= 10; i++) {
              const reviewField = `review${i}` as keyof typeof reviewData;
              if (reviewData[reviewField] && typeof reviewData[reviewField] === 'string') {
                reviewsArray.push(reviewData[reviewField] as string);
              }
            }
            
            let rating = school.googleReview;
            let count = school.reviewCount;
            
            if (reviewData["google_maps_reviews.google_maps_reviews.rating"]) {
              rating = parseFloat(reviewData["google_maps_reviews.google_maps_reviews.rating"] as string) || rating;
            }
            
            if (reviewData["google_maps_reviews.google_maps_reviews.reviews"]) {
              count = parseInt(reviewData["google_maps_reviews.google_maps_reviews.reviews"] as string) || count;
            }
            
            console.log(`Extracted ${reviewsArray.length} reviews, rating: ${rating}, count: ${count}`);
            
            setReviewData({
              reviews: reviewsArray,
              googleReview: rating,
              reviewCount: count
            });
            return;
          }
          
          const { data: schoolData, error: schoolError } = await supabase
            .from('hundeschuleList')
            .select('*')
            .eq('google_id', school.id)
            .maybeSingle();
          
          if (schoolError) {
            console.error("Error fetching school data:", schoolError);
            return;
          }
          
          if (schoolData) {
            console.log("Found school data with reviews:", schoolData);
            
            const reviewsArray: string[] = [];
            
            for (let i = 1; i <= 10; i++) {
              const reviewField = `review${i}` as keyof typeof schoolData;
              if (schoolData[reviewField] && typeof schoolData[reviewField] === 'string') {
                reviewsArray.push(schoolData[reviewField] as string);
              }
            }
            
            let rating = school.googleReview;
            let count = school.reviewCount;
            
            if (schoolData.reviewsrating) {
              const ratingString = schoolData.reviewsrating.toString().replace(',', '.');
              rating = parseFloat(ratingString) || rating;
            }
            
            if (schoolData.reviewscount) {
              count = parseInt(schoolData.reviewscount) || count;
            } else if (schoolData.reviews) {
              count = parseInt(schoolData.reviews) || count;
            }
            
            console.log(`Extracted ${reviewsArray.length} reviews from school data, rating: ${rating}, count: ${count}`);
            
            setReviewData({
              reviews: reviewsArray,
              googleReview: rating,
              reviewCount: count
            });
          }
        }
      } catch (error) {
        console.error("Error in fetchReviews:", error);
      }
    };
    
    fetchReviews();
  }, [school.id]);

  useEffect(() => {
    const reviewItems = reviewData.reviews.map((review, index) => {
      return {
        "@type": "Review",
        "reviewRating": {
          "@type": "Rating",
          "ratingValue": "5",
          "bestRating": "5",
          "worstRating": "1"
        },
        "author": {
          "@type": "Person",
          "name": `Kunde ${index + 1}`
        },
        "reviewBody": review.substring(0, 250),
        "datePublished": new Date().toISOString().split('T')[0]
      };
    });
    
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `https://hundeschulenfinder.de/school/${school.id}`,
      "name": school.name,
      "image": [
        school.imageUrl || "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3"
      ],
      "logo": {
        "@type": "ImageObject",
        "url": "https://hundeschulenfinder.de/lovable-uploads/5dcadb59-e79b-45de-851e-4a6add76a6cc.png",
        "width": "180",
        "height": "60"
      },
      "address": {
        "@type": "PostalAddress",
        "streetAddress": school.address || "",
        "addressLocality": school.city || "",
        "postalCode": school.postalCode || "",
        "addressCountry": school.country || "Germany"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": school.location?.lat || 51.1657,
        "longitude": school.location?.lng || 10.4515
      },
      "url": school.website || `https://hundeschulenfinder.de/school/${school.id}`,
      "telephone": school.phoneNumber || "",
      "priceRange": "$$",
      "description": `${school.name} ist eine Hundeschule in ${school.city}, ${school.state}. Wir bieten professionelles Hundetraining für Welpen, Junghunde und erwachsene Hunde.`,
      "review": reviewItems.length > 0 ? reviewItems : undefined,
      "aggregateRating": reviewItems.length > 0 ? {
        "@type": "AggregateRating",
        "ratingValue": reviewData.googleReview.toFixed(1),
        "bestRating": "5",
        "worstRating": "1",
        "ratingCount": reviewData.reviewCount > 0 ? reviewData.reviewCount.toString() : "12",
        "reviewCount": reviewData.reviewCount > 0 ? reviewData.reviewCount.toString() : "12",
        "itemReviewed": {
          "@type": "LocalBusiness",
          "name": school.name
        }
      } : undefined,
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "09:00",
          "closes": "18:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Saturday"],
          "opens": "10:00",
          "closes": "16:00"
        }
      ],
      "photo": [
        {
          "@type": "ImageObject",
          "url": school.imageUrl || "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3",
          "width": "800",
          "height": "600",
          "caption": `${school.name} - Hundeschule in ${school.city}`
        }
      ],
      "hasMap": `https://www.google.com/maps?q=${school.location?.lat || ''}, ${school.location?.lng || ''}`,
      "isAccessibleForFree": "True",
      "sameAs": school.website ? [school.website] : []
    };
    
    const schoolSchemaId = `school-schema-${school.id}`;
    
    const existingScript = document.getElementById(schoolSchemaId);
    if (existingScript) existingScript.remove();
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = schoolSchemaId;
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(schoolSchemaId);
      if (scriptToRemove) scriptToRemove.remove();
    };
  }, [school, reviewData]);

  const openGoogleMaps = () => {
    let mapUrl;
    
    const fullAddress = [
      school.name,
      school.address,
      `${school.postalCode} ${school.city}`,
      school.country
    ].filter(Boolean).join(', ');
    
    console.log(`Opening Google Maps with address: ${fullAddress}`);
    
    if (school.location && 
        school.location.lat && 
        school.location.lng && 
        !(school.location.lat === 51.1657 && school.location.lng === 10.4515)) {
      mapUrl = `https://www.google.com/maps?q=${school.location.lat},${school.location.lng}`;
      console.log(`Using coordinates: ${school.location.lat},${school.location.lng}`);
    } else {
      const formattedAddress = encodeURIComponent(fullAddress);
      mapUrl = `https://www.google.com/maps/search/?api=1&query=${formattedAddress}`;
      console.log(`Using formatted address: ${formattedAddress}`);
    }
    
    window.open(mapUrl, '_blank');
  };

  const formattedAddress = school.address ? 
    `${school.address}, ${school.postalCode} ${school.city}` : 
    `${school.postalCode} ${school.city}`;
    
  const schoolImageAlt = `${school.name} - Hundeschule in ${school.city}, ${school.state} - ${school.googleReview.toFixed(1)} Sterne Bewertung mit ${reviewData.reviewCount} Bewertungen`;

  return (
    <div className="w-full relative z-10">
      <div className="flex flex-col lg:flex-row gap-6">
        <Card className="flex-1 overflow-hidden hover:shadow-lg transition-shadow duration-300 border border-gray-200 rounded-lg">
          <div className="relative h-56 overflow-hidden bg-gray-100 flex items-center justify-center">
            <img 
              src={school.imageUrl || "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3"}
              alt={schoolImageAlt}
              className="w-full h-full object-contain"
              onError={(e) => {
                e.currentTarget.src = "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?ixlib=rb-4.0.3";
                e.currentTarget.alt = `Platzhalter Bild für ${school.name} - Hundeschule in ${school.city}`;
                setImageError(true);
              }}
            />
            <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center space-x-1 shadow-sm">
              <Star className="h-4 w-4 fill-yellow-400 stroke-yellow-400" />
              <span className="text-sm font-medium">{reviewData.googleReview.toFixed(1)}</span>
              <span className="text-xs text-gray-500">({reviewData.reviewCount || "0"})</span>
            </div>
          </div>
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mb-3">
              <h3 className="text-xl font-semibold">{school.name}</h3>
              {distance !== undefined && (
                <span className="text-sm text-gray-500">
                  ({distance.toFixed(1)} km entfernt)
                </span>
              )}
            </div>
            
            <Separator className="my-4" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SchoolBasicInfo school={school} formattedAddress={formattedAddress} />
              <SchoolFeatures school={school} />
            </div>
            
            <SchoolActionButtons website={school.website} onMapClick={openGoogleMaps} />
          </CardContent>
        </Card>

        <div className="w-full lg:w-[400px] space-y-4">
          <ReviewCarousel reviews={reviewData.reviews} />
        </div>
      </div>
    </div>
  );
};

const areEqual = (prevProps: DogSchoolCardProps, nextProps: DogSchoolCardProps) => {
  return (
    prevProps.school.id === nextProps.school.id &&
    prevProps.distance === nextProps.distance
  );
};

export const DogSchoolCard = memo(DogSchoolCardComponent, areEqual);
